/**
 * Fetch Saved Payment Method Actor
 *
 * Retrieves a customer's saved payment method from Stripe.
 * Used to check if a returning MFA'd user has a card on file.
 */

import { fromPromise } from "xstate";
import { z } from "zod";

/**
 * Input Schema (What this actor needs)
 */
const FetchSavedPaymentMethodInputSchema = z.object({
  consumerId: z.string(),
});

type FetchSavedPaymentMethodInput = z.infer<
  typeof FetchSavedPaymentMethodInputSchema
>;

/**
 * Saved Payment Method Schema
 */
const SavedPaymentMethodSchema = z.object({
  id: z.string(),
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number(),
  expYear: z.number(),
});

/**
 * Success Schema (What this actor returns on success)
 */
const FetchSavedPaymentMethodSuccessSchema = z.object({
  paymentMethod: SavedPaymentMethodSchema.nullable(),
});

type FetchSavedPaymentMethodSuccess = z.infer<
  typeof FetchSavedPaymentMethodSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const FetchSavedPaymentMethodErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "NETWORK_ERROR",
    "BACKEND_ERROR",
    "UNAUTHORIZED",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
});

type FetchSavedPaymentMethodError = z.infer<
  typeof FetchSavedPaymentMethodErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "data" | "communication" | "auth" | "payment";
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  impl: ReturnType<
    typeof fromPromise<
      FetchSavedPaymentMethodSuccess,
      FetchSavedPaymentMethodInput
    >
  >;
  metadata: ActorMetadata;
}

/**
 * Fetch Saved Payment Method Actor Implementation
 *
 * Retrieves a customer's saved payment method from Stripe.
 */
export const fetchSavedPaymentMethodActor: ActorWithMetadata = {
  impl: fromPromise<
    FetchSavedPaymentMethodSuccess,
    FetchSavedPaymentMethodInput
  >(async ({ input }) => {
    // Validate input
    const validated = FetchSavedPaymentMethodInputSchema.parse(input);

    try {
      // Call API to get saved payment method
      const response = await fetch(
        `/api/stripe/get-payment-method?consumerId=${encodeURIComponent(validated.consumerId)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw {
            code: "UNAUTHORIZED",
            message: "Not authorized to fetch payment method",
            retryable: false,
          };
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Validate response
      const result = FetchSavedPaymentMethodSuccessSchema.parse(data);

      return result;
    } catch (error) {
      // If already formatted error, rethrow
      if (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        "message" in error
      ) {
        throw error;
      }

      // Parse error
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      const parsedError: FetchSavedPaymentMethodError = {
        code: "BACKEND_ERROR",
        message: errorMessage,
        retryable: true,
        details: error,
      };

      throw parsedError;
    }
  }),
  metadata: {
    displayName: "fetchSavedPaymentMethod",
    description: "Fetch customer's saved payment method from Stripe",
    category: "payment",
    inputSchema: FetchSavedPaymentMethodInputSchema,
    successSchema: FetchSavedPaymentMethodSuccessSchema,
    errorSchema: FetchSavedPaymentMethodErrorSchema,
  },
};

// Export the schema for use in components/context
export { SavedPaymentMethodSchema };
export type { FetchSavedPaymentMethodSuccess };
